import { useState, useCallback } from 'react';
import { toast } from '@/hooks/useToast';
import { useI18n } from '@/hooks/useI18n';

export interface ManualFetchResult<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  /** Da agganciare al bottone: nessun caricamento automatico al mount. */
  load: () => Promise<void>;
}

/**
 * Ciclo di vita condiviso dalle sezioni diagnostiche dell'admin: i dati NON
 * vengono caricati al mount (sono query costose), ma solo quando l'utente
 * chiama `load()`. Una volta caricati restano in memoria finché non si
 * richiama `load()` per ricaricarli.
 *
 * Il `fetcher` deve essere stabile (`useCallback`) e lanciare in caso di
 * errore: qui vengono centralizzati stato di loading, messaggio d'errore
 * localizzato e toast.
 *
 * @param context etichetta usata solo nel log di errore, per capire da quale
 *                sezione arriva il fallimento.
 */
export function useManualFetch<T>(
  fetcher: () => Promise<T>,
  context: string
): ManualFetchResult<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { t } = useI18n();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      setData(await fetcher());
    } catch (err) {
      console.error(`[${context}] load failed`, err);
      const msg = String(t('errors.loadFailed'));
      setError(msg);
      toast({
        title: String(t('common.error')),
        description: msg,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [fetcher, context, t]);

  return { data, loading, error, load };
}

export default useManualFetch;
